import { ApiError, matricesApi, matrixGenerationApi } from '@/lib/api';
import { useDebouncedCallback } from '@/lib/use-debounced-callback';
import {
  type Dimension,
  MATRIX_PIVOT_LABELS,
  type MatrixDestinataire,
  type MatrixPivot,
  type MatrixState,
  PALIER_NUMBERS,
  type PalierNumber,
  type PatchMatrixInput,
  type ResolvedPalier,
  type UserRole,
  palierPosition,
} from '@mss_creator/shared';
import { Alert, Button, Icon, TextArea } from '@pms-digital/design-system';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DestinataireField } from './destinataire-field';

const AUTOSAVE_DEBOUNCE_MS = 500;

interface RecapitulatifStepProps {
  matrixId: string;
  companyId: string;
  /** The pivot chosen at step 1, recalled read-only in the summary (issue #16). */
  pivot: MatrixPivot;
  /** The recipient as last saved on the matrix; null = « Non défini » (issue #25). */
  destinataire: MatrixDestinataire | null;
  /** The free text of « Autre » as last saved on the matrix (issue #21). */
  destinataireAutre: string | null;
  /** `paliersFrozenAt` is set: the recipient froze with the paliers (règle complémentaire 1). */
  optionsFrozen: boolean;
  sujet: string | null;
  contexte: string | null;
  dimensions: Dimension[];
  /** The matrix's resolved palier table (issue #1): the labels « Paliers à générer » lists next to each position. */
  paliers: ResolvedPalier[];
  matrixState: MatrixState;
  /** True only while `matrixState === 'brouillon'` and the most recent attempt failed — see `matrices.lastGenerationFailedAt`. */
  lastGenerationFailed: boolean;
  role: UserRole;
  /** True while this is the wizard's current step — drives the "return to step 4" check. */
  active: boolean;
  /** The company is archived (issue #22): the summary stays readable, nothing can be changed or generated. */
  readOnly?: boolean;
  onBack: () => void;
  /** Called once the `POST /generation-jobs` request itself succeeds — lets the parent invalidate the matrix query right away. */
  onGenerationStarted: () => void;
}

/**
 * Turns a failed `POST /matrices/:id/generation-jobs` into a short French
 * message — the button's own disabled-while-pending state closes the
 * double-click race that would otherwise produce the 409 case here, but a
 * slow click or a genuine 422/500/network failure can still reach this.
 * Backend error messages are English, so they aren't surfaced verbatim.
 */
function toGenerationSubmitError(error: unknown): string {
  if (error instanceof ApiError && error.status === 409) {
    return 'Une génération est déjà en cours pour cette matrice.';
  }
  return 'Impossible de lancer la génération. Réessayez.';
}

/** The generation guard's 422: the setup was emptied since the pivot was chosen. */
function isPivotNotFilled(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'PIVOT_NOT_FILLED';
}

function destinataireErrorMessage(error: unknown): string {
  if (error instanceof ApiError && error.code === 'MATRIX_PIVOT_FROZEN') {
    return 'Le destinataire est figé depuis la première génération : le destinataire enregistré a été rétabli.';
  }
  return "Le destinataire n'a pas pu être enregistré : le destinataire enregistré a été rétabli. Réessayez.";
}

/**
 * Step 4 — récapitulatif. Three visual modes: editable (normal brouillon —
 * summary, palier selection, optional prompt reveal, "Générer"), locked
 * (génération_en_cours — same read-only summary plus a progress message, no
 * Générer/Retour), and failed (brouillon with lastGenerationFailed — the
 * normal editable mode plus a failure block, Générer re-enabled to retry).
 *
 * The composed prompt is hidden by default and only shown/fetched for
 * `pms_admin` users (server-enforced too — see `GET /matrices/:id/composed-prompt`).
 * It's re-fetched whenever the palier selection changes while revealed and
 * not manually edited, so the "(non généré)" markers stay in sync with what
 * "Générer" would actually submit. Tracks whether the user has diverged the
 * textarea from the last fetched/composed text (`promptManuallyEdited`,
 * computed live); returning to this step with a manual edit still pending
 * prompts an explicit choice instead of silently picking a side.
 *
 * The recipient (issues #16, #21, #25) is saved on each change (the free text
 * of « Autre » after a short debounce like sujet and contexte; « Générer »
 * flushes it and waits for it), and the same PATCH clears any stored
 * `initialPromptOverride` - the rule the pivot follows at step 1: that text
 * has the previous recipient already resolved in it. « Autre » left empty is
 * « Non défini », not an error.
 * Once saved, the revealed prompt is recomposed like a palier change, or, if
 * an edit is on screen, the « Conserver / Régénérer » choice is asked.
 */
export function RecapitulatifStep({
  matrixId,
  companyId,
  pivot,
  destinataire: savedDestinataireProp,
  destinataireAutre: savedAutreProp,
  optionsFrozen,
  sujet,
  contexte,
  dimensions,
  paliers,
  matrixState,
  lastGenerationFailed,
  role,
  active,
  readOnly = false,
  onBack,
  onGenerationStarted,
}: RecapitulatifStepProps) {
  const [promptRevealed, setPromptRevealed] = useState(false);
  const [promptText, setPromptText] = useState('');
  const [lastFetchedPrompt, setLastFetchedPrompt] = useState<string | null>(null);
  const [pendingConflict, setPendingConflict] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [selectedPaliers, setSelectedPaliers] = useState<Set<PalierNumber>>(
    new Set(PALIER_NUMBERS),
  );
  const queryClient = useQueryClient();
  const [destinataire, setDestinataire] = useState<MatrixDestinataire | null>(
    savedDestinataireProp,
  );
  const [savedDestinataire, setSavedDestinataire] = useState<MatrixDestinataire | null>(
    savedDestinataireProp,
  );
  const [autre, setAutre] = useState(savedAutreProp ?? '');
  const [savedAutre, setSavedAutre] = useState(savedAutreProp ?? '');
  const [destinataireError, setDestinataireError] = useState<string | null>(null);
  // Saves go out one at a time, in order; `autreEditPendingRef` is true while a
  // keystroke waits in the debounce, `pendingSavesRef` while a save is queued or in flight.
  const saveChainRef = useRef<Promise<boolean>>(Promise.resolve(true));
  const pendingSavesRef = useRef(0);
  const autreEditPendingRef = useRef(false);

  const isGenerating = matrixState === 'génération_en_cours';
  const canShowPromptToggle = role === 'pms_admin';
  const promptManuallyEdited = lastFetchedPrompt !== null && promptText !== lastFetchedPrompt;

  // Column order, whatever order the API listed them in.
  const sortedPaliers = useMemo(
    () => [...paliers].sort((a, b) => palierPosition(a.number) - palierPosition(b.number)),
    [paliers],
  );

  const sortedSelected = useMemo(
    () => [...selectedPaliers].sort((a, b) => a - b),
    [selectedPaliers],
  );

  const { refetch, isFetching } = useQuery({
    queryKey: ['composed-prompt', matrixId, sortedSelected.join(',')],
    queryFn: () => matricesApi.getComposedPrompt(matrixId, sortedSelected),
    enabled: false,
  });

  const fetchAndApply = useCallback(async () => {
    setFetchError(null);
    const result = await refetch();
    if (result.data) {
      setPromptText(result.data.prompt);
      setLastFetchedPrompt(result.data.prompt);
    } else if (result.error) {
      const message =
        result.error instanceof ApiError && result.error.code === 'ROLE_FORBIDDEN'
          ? "Vous n'avez pas les droits pour afficher ce prompt."
          : 'Impossible de charger le prompt composé pour le moment.';
      setFetchError(message);
    }
  }, [refetch]);

  const wasActiveRef = useRef(active);
  useEffect(() => {
    const justEntered = active && !wasActiveRef.current;
    wasActiveRef.current = active;
    if (!justEntered || !promptRevealed) return;
    if (promptManuallyEdited) {
      setPendingConflict(true);
    } else {
      void fetchAndApply();
    }
  }, [active, promptRevealed, promptManuallyEdited, fetchAndApply]);

  // Re-fetch when the palier selection changes, but only if the prompt is
  // both revealed and not manually diverged — a manual edit wins until the
  // user explicitly regenerates (same rule as the "return to step 4"
  // conflict flow above), so a live toggle never silently clobbers it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed deliberately on sortedSelected's serialized value only — fetchAndApply/promptRevealed/promptManuallyEdited are read at the moment of the change, not watched for their own sake (including them would refetch on every unrelated render once revealed).
  useEffect(() => {
    if (!promptRevealed || promptManuallyEdited) return;
    void fetchAndApply();
  }, [sortedSelected.join(',')]);

  function handleToggleReveal() {
    const next = !promptRevealed;
    setPromptRevealed(next);
    if (next && lastFetchedPrompt === null) {
      void fetchAndApply();
    }
  }

  function handleRegenerate() {
    setPendingConflict(false);
    void fetchAndApply();
  }

  function handleKeepManualEdit() {
    setPendingConflict(false);
  }

  const destinataireMutation = useMutation({
    mutationFn: (patch: PatchMatrixInput) =>
      matricesApi.patch(matrixId, { ...patch, initialPromptOverride: null }),
    onSuccess: (updated) => {
      setSavedDestinataire(updated.destinataire);
      setSavedAutre(updated.destinataireAutre ?? '');
      void queryClient.invalidateQueries({ queryKey: ['matrix', matrixId] });
      // Recompose only once the server holds the new value: the composed
      // prompt is built from the saved matrix, not from this screen.
      if (!promptRevealed) return;
      if (promptManuallyEdited) {
        setPendingConflict(true);
      } else {
        void fetchAndApply();
      }
    },
    onError: (error, patch) => {
      if (patch.destinataire !== undefined) setDestinataire(savedDestinataire);
      if (patch.destinataireAutre !== undefined) setAutre(savedAutre);
      setDestinataireError(destinataireErrorMessage(error));
      // The 409 means the matrix froze meanwhile: a fresh copy turns the
      // radios read-only.
      void queryClient.invalidateQueries({ queryKey: ['matrix', matrixId] });
    },
  });

  function queueSave(patch: PatchMatrixInput) {
    pendingSavesRef.current += 1;
    saveChainRef.current = saveChainRef.current
      .then(() =>
        destinataireMutation.mutateAsync(patch).then(
          () => true,
          () => false,
        ),
      )
      .finally(() => {
        pendingSavesRef.current -= 1;
      });
  }

  const autosaveAutre = useDebouncedCallback((text: string) => {
    autreEditPendingRef.current = false;
    queueSave({ destinataireAutre: text });
  }, AUTOSAVE_DEBOUNCE_MS);

  function handleDestinataireChange(next: MatrixDestinataire | null) {
    setDestinataireError(null);
    setDestinataire(next);
    queueSave({ destinataire: next });
  }

  function handleAutreChange(text: string) {
    setDestinataireError(null);
    setAutre(text);
    autreEditPendingRef.current = true;
    autosaveAutre(text);
  }

  function handleTogglePalier(number: PalierNumber) {
    setSelectedPaliers((previous) => {
      const next = new Set(previous);
      if (next.has(number)) {
        next.delete(number);
      } else {
        next.add(number);
      }
      return next;
    });
  }

  const latestJobQuery = useQuery({
    queryKey: ['generation-job', matrixId],
    queryFn: () => matrixGenerationApi.getLatest(matrixId),
    enabled: lastGenerationFailed,
  });

  const finalizeMutation = useMutation({
    mutationFn: (initialPromptOverride: string | null) =>
      matricesApi.patch(matrixId, { initialPromptOverride }),
  });
  const startGenerationMutation = useMutation({
    mutationFn: (columnNumbers: PalierNumber[]) =>
      matrixGenerationApi.start(matrixId, { columnNumbers }),
    onSuccess: onGenerationStarted,
  });
  const pivotNotFilled =
    startGenerationMutation.isError && isPivotNotFilled(startGenerationMutation.error);
  const submitError =
    startGenerationMutation.isError && !pivotNotFilled
      ? toGenerationSubmitError(startGenerationMutation.error)
      : null;

  const inFlight =
    isGenerating ||
    startGenerationMutation.isPending ||
    finalizeMutation.isPending ||
    destinataireMutation.isPending;
  // « Autre » with nothing typed is « Non défini »: it neither shows an error nor blocks « Générer ».
  const canGenerate =
    !readOnly &&
    !inFlight &&
    Boolean(sujet?.trim()) &&
    Boolean(contexte?.trim()) &&
    selectedPaliers.size > 0;

  function handleGenerate() {
    if (!canGenerate) return;
    // The server reads the recipient from the saved matrix: a keystroke still
    // in the debounce is sent now, and the generation starts once it has landed.
    const mustWaitForSave = autreEditPendingRef.current || pendingSavesRef.current > 0;
    autosaveAutre.flush();
    if (mustWaitForSave) {
      void saveChainRef.current.then((saved) => {
        if (saved) launchGeneration();
      });
      return;
    }
    launchGeneration();
  }

  function launchGeneration() {
    const override = promptManuallyEdited ? promptText : null;
    if (override !== null) {
      finalizeMutation.mutate(override, {
        onSuccess: () => startGenerationMutation.mutate(sortedSelected),
      });
    } else {
      startGenerationMutation.mutate(sortedSelected);
    }
  }

  const orderedDimensions = [...dimensions].sort((a, b) => a.displayOrder - b.displayOrder);
  const showSplitLayout = canShowPromptToggle && promptRevealed;

  return (
    <section className="space-y-6">
      <div>
        <h2 className="font-semibold text-h3">Récapitulatif</h2>
        <p className="text-gray-600 text-small">
          {isGenerating
            ? 'Génération en cours — la matrice est verrouillée pendant ce temps.'
            : readOnly
              ? "Entreprise archivée : la matrice ne peut pas être générée tant qu'elle n'est pas restaurée."
              : 'Vérifiez les informations avant de générer la matrice.'}
        </p>
      </div>

      <div className={showSplitLayout ? 'grid grid-cols-[2fr_3fr] gap-6' : ''}>
        <div className="space-y-6">
          <div className="space-y-2 rounded-lg bg-gray-50 p-4">
            <div>
              <p className="font-semibold text-caption text-gray-500 uppercase tracking-wide">
                Sujet
              </p>
              <p className="text-small">{sujet || '—'}</p>
            </div>
            <div>
              <p className="font-semibold text-caption text-gray-500 uppercase tracking-wide">
                Contexte
              </p>
              <p className="whitespace-pre-wrap text-small">{contexte || '—'}</p>
            </div>
            <div>
              <p className="font-semibold text-caption text-gray-500 uppercase tracking-wide">
                Pivot
              </p>
              <p className="text-small">
                {MATRIX_PIVOT_LABELS[pivot]}{' '}
                <span className="text-gray-500">· choisi à l'étape 1</span>
              </p>
            </div>
          </div>

          <div className="space-y-2 rounded-lg bg-gray-50 p-4">
            <p className="font-semibold text-caption text-gray-500 uppercase tracking-wide">
              Dimensions
            </p>
            {orderedDimensions.length === 0 ? (
              <p className="text-small">—</p>
            ) : (
              <ul className="space-y-1">
                {orderedDimensions.map((dimension) => (
                  <li key={dimension.id} className="text-small">
                    <span className="font-semibold">{dimension.title}</span>
                    {dimension.description && (
                      <span className="text-gray-500"> — {dimension.description}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {!(sujet?.trim() && contexte?.trim()) && (
            <Alert tone="danger">
              Le sujet et le contexte doivent être renseignés (retournez à l'étape 2).
            </Alert>
          )}

          <section
            aria-labelledby={`${matrixId}-options-title`}
            className="flex flex-col gap-4 rounded-lg border-2 border-gray-100 bg-white px-6 py-4"
          >
            <div>
              <h3 id={`${matrixId}-options-title`} className="font-semibold text-body">
                Options de génération
              </h3>
              <p className="mt-0 text-gray-600 text-small">
                {optionsFrozen
                  ? 'Le destinataire est figé depuis la première génération.'
                  : 'Le destinataire est enregistré à chaque changement et figé au lancement de la génération.'}
              </p>
            </div>
            <fieldset className="space-y-2" disabled={inFlight || readOnly}>
              <legend className="font-semibold text-small">Paliers à générer</legend>
              {/* Issue #28 (planche 21): one line per palier, « position · label » —
                  the position among the 8 columns, never the internal number. The
                  label is cut to one line, whole on hover: with custom paliers it is
                  the only way to see which ones are left aside. */}
              <div className="flex flex-col gap-1">
                {sortedPaliers.map((palier) => {
                  const position = palierPosition(palier.number);
                  return (
                    <label key={palier.number} className="flex items-center gap-1 text-small">
                      <input
                        type="checkbox"
                        aria-label={`Palier ${position} · ${palier.label}`}
                        checked={selectedPaliers.has(palier.number)}
                        onChange={() => handleTogglePalier(palier.number)}
                        className="h-4 w-4 shrink-0 rounded border-gray-300 text-bleu-nuit focus:ring-bleu-nuit"
                      />
                      <span className="shrink-0">
                        <span className="font-semibold">{position}</span> ·
                      </span>{' '}
                      <span title={palier.label} className="max-w-60 truncate text-gray-600">
                        {palier.label}
                      </span>
                    </label>
                  );
                })}
              </div>
            </fieldset>
            <hr className="h-0.5 border-0 bg-gray-100" />
            <div>
              <DestinataireField
                value={destinataire}
                autre={autre}
                disabled={
                  readOnly || optionsFrozen || isGenerating || startGenerationMutation.isPending
                }
                onChange={handleDestinataireChange}
                onAutreChange={handleAutreChange}
              />
              {destinataireError && <Alert tone="danger">{destinataireError}</Alert>}
            </div>
          </section>

          {canShowPromptToggle && (
            <Button type="button" variant="primary" onClick={handleToggleReveal}>
              {promptRevealed ? (
                <Icon name="visibility_off" size={16} />
              ) : (
                <Icon name="visibility" size={16} />
              )}
              {promptRevealed ? 'Masquer le prompt' : 'Afficher le prompt'}
            </Button>
          )}

          {isGenerating && (
            <p className="flex items-center gap-2 text-gray-600 text-small">
              <span
                aria-hidden="true"
                className="h-4 w-4 animate-spin rounded-full border-2 border-bleu-nuit border-t-transparent"
              />
              Génération en cours, cela peut prendre un moment…
            </p>
          )}

          {lastGenerationFailed && latestJobQuery.data?.error && (
            <div className="text-danger text-small">
              <p>
                La génération a échoué. Réessayez, ou contactez un administrateur si le problème
                persiste.
              </p>
              <details className="mt-1 text-caption text-gray-500">
                <summary className="cursor-pointer">Détails techniques</summary>
                <p className="mt-1 whitespace-pre-wrap font-mono">{latestJobQuery.data.error}</p>
              </details>
            </div>
          )}

          {submitError && <p className="text-danger text-small">{submitError}</p>}
          {pivotNotFilled && (
            <p className="text-danger text-small">
              Le pivot « {MATRIX_PIVOT_LABELS[pivot]} » n'est plus renseigné pour les 6 étapes du
              setup de l'entreprise : la génération ne peut pas démarrer.{' '}
              {optionsFrozen
                ? 'Complétez le setup, puis relancez la génération.'
                : "Complétez le setup, ou choisissez un autre pivot à l'étape 1."}{' '}
              <a
                href={`/companies/${companyId}`}
                className="font-semibold text-bleu-nuit underline-offset-2 hover:underline"
              >
                Ouvrir la fiche entreprise
              </a>
            </p>
          )}

          <div className="sticky bottom-0 flex justify-between bg-white pt-4">
            {!isGenerating && (
              <Button type="button" variant="primary" onClick={onBack}>
                Retour
              </Button>
            )}
            {!isGenerating && (
              <Button
                variant="secondary"
                type="button"
                onClick={handleGenerate}
                disabled={!canGenerate}
              >
                Générer
              </Button>
            )}
          </div>
        </div>

        {showSplitLayout && (
          <div className="space-y-3">
            {pendingConflict && (
              <div className="space-y-3 rounded-lg border-2 border-warning bg-warning-tint p-4">
                <p className="text-small">
                  Vous avez modifié le prompt manuellement. Conserver votre édition ou régénérer à
                  partir des nouvelles données ?
                </p>
                <div className="flex gap-2">
                  <Button type="button" size="sm" variant="primary" onClick={handleKeepManualEdit}>
                    Conserver mon édition
                  </Button>
                  <Button variant="secondary" type="button" size="sm" onClick={handleRegenerate}>
                    Régénérer
                  </Button>
                </div>
              </div>
            )}

            {!pendingConflict && (
              <div>
                {isFetching && <p className="text-gray-500 text-small">Composition du prompt…</p>}
                {fetchError && <Alert tone="danger">{fetchError}</Alert>}
                {!isFetching && !fetchError && (
                  <TextArea
                    label={<span>Prompt composé</span>}
                    value={promptText}
                    onChange={(event) => setPromptText(event.target.value)}
                    disabled={isGenerating}
                  />
                )}
                {promptManuallyEdited && !pendingConflict && (
                  <p className="mt-1 text-caption text-gray-500">
                    Prompt édité manuellement — cette version sera enregistrée au prochain
                    "Générer".
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
